import React, { useEffect, useRef, useState } from "react";

import {
  IMAGE_ACCEPT,
  IMAGE_ACCEPT_HINT,
  UNSUPPORTED_IMAGE_MESSAGE,
  canLocalPreview,
  isAcceptedImageFile,
  isHeicLike,
} from "../../../shared/imageFileContract";
import AvatarCropEditor from "../../../shared/avatarCrop/AvatarCropEditor";
import { isProfileCropEligible } from "../../../shared/avatarCrop/avatarCropEligibility";
import { SERVER_PREVIEW_CONFIRM_MESSAGE } from "../../../shared/mediaPrepare/constants";
import {
  createPrepareSession,
  mapPrepareErrorMessage,
  prepareImageForEditor,
} from "../../../shared/mediaPrepare/prepareImageForEditor";

function ProfilePhotoSection({
  onUserImageChange,
  selectedFile = null,
  existingImageUrl = null,
  uploadStatus = {},
  onCropActiveChange,
}) {
  const { loading = false, success = false, error = null } = uploadStatus;
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadSuccessLatched, setUploadSuccessLatched] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingCropSource, setPendingCropSource] = useState(null);
  const [prepareStatus, setPrepareStatus] = useState(null);
  const fileInputRef = useRef(null);
  const prepareSessionRef = useRef(null);
  const activeRunIdRef = useRef(0);

  const cropActive = Boolean(pendingCropSource);
  const preparePending = Boolean(
    prepareStatus &&
      (prepareStatus.state === "native_check_pending" ||
        prepareStatus.state === "server_preview_pending" ||
        prepareStatus.state === "selected"),
  );
  const showLocalPreview = Boolean(
    selectedFile && canLocalPreview(selectedFile) && previewUrl && !cropActive,
  );

  useEffect(() => {
    onCropActiveChange?.(cropActive || preparePending);
  }, [cropActive, preparePending, onCropActiveChange]);

  useEffect(() => {
    if (!selectedFile || !canLocalPreview(selectedFile)) {
      setPreviewUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  useEffect(() => {
    if (success) {
      setUploadSuccessLatched(true);
    }
  }, [success]);

  useEffect(() => {
    if (selectedFile) {
      setUploadSuccessLatched(false);
      setLocalError(null);
    }
  }, [selectedFile]);

  useEffect(
    () => () => {
      // Unmount: abort active prepare and invalidate run id so late completions
      // cannot emit native_ready / commit editor state after owner teardown.
      prepareSessionRef.current?.abort();
      prepareSessionRef.current = null;
      activeRunIdRef.current += 1;
    },
    [],
  );

  const clearNativeInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const abortPrepare = () => {
    prepareSessionRef.current?.abort();
    prepareSessionRef.current = null;
    activeRunIdRef.current += 1;
  };

  const startHeicPrepare = async (file) => {
    abortPrepare();
    const session = createPrepareSession();
    prepareSessionRef.current = session;
    activeRunIdRef.current = session.runId;
    setLocalError(null);
    setPrepareStatus({ state: "selected", runId: session.runId });
    setPendingCropSource(null);
    // Never silently queue the original HEIC for final upload.
    onUserImageChange(null);

    const result = await prepareImageForEditor(file, {
      surface: "profile_avatar",
      runId: session.runId,
      signal: session.signal,
      confirmMessage: SERVER_PREVIEW_CONFIRM_MESSAGE,
      isCurrent: () => activeRunIdRef.current === session.runId,
      onStateChange: (next) => {
        if (activeRunIdRef.current !== session.runId) return;
        setPrepareStatus(next);
      },
    });

    if (activeRunIdRef.current !== session.runId) {
      return;
    }

    if (result.kind === "native" || result.kind === "server") {
      setPrepareStatus({ state: result.state, runId: session.runId });
      setPendingCropSource(result.file);
      setLocalError(null);
      return;
    }

    if (result.kind === "cancelled") {
      setPrepareStatus(null);
      setPendingCropSource(null);
      return;
    }

    setPrepareStatus(null);
    setPendingCropSource(null);
    setLocalError(mapPrepareErrorMessage(result.code, result.detail));
  };

  const validateAndSelectFile = (file) => {
    if (!file) {
      abortPrepare();
      setLocalError(null);
      setPrepareStatus(null);
      setPendingCropSource(null);
      onUserImageChange(null);
      return;
    }

    if (!isAcceptedImageFile(file)) {
      abortPrepare();
      setLocalError(UNSUPPORTED_IMAGE_MESSAGE);
      setPrepareStatus(null);
      setPendingCropSource(null);
      onUserImageChange(null);
      clearNativeInput();
      return;
    }

    setLocalError(null);

    if (isHeicLike(file)) {
      clearNativeInput();
      startHeicPrepare(file);
      return;
    }

    if (isProfileCropEligible(file)) {
      abortPrepare();
      setPrepareStatus(null);
      setPendingCropSource(file);
      clearNativeInput();
      return;
    }

    abortPrepare();
    setLocalError(UNSUPPORTED_IMAGE_MESSAGE);
    setPrepareStatus(null);
    setPendingCropSource(null);
    onUserImageChange(null);
    clearNativeInput();
  };

  const handleChange = (event) => {
    const file = event.target.files && event.target.files[0];
    validateAndSelectFile(file || null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    validateAndSelectFile(file || null);
  };

  const handleCropApply = (croppedFile) => {
    setPendingCropSource(null);
    setPrepareStatus(null);
    onUserImageChange(croppedFile);
  };

  const handleCropCancel = () => {
    setPendingCropSource(null);
    setPrepareStatus(null);
    // Retain previously applied selectedFile / server image; do not clear.
  };

  const displayImageUrl =
    showLocalPreview
      ? previewUrl
      : (!selectedFile && !cropActive && !preparePending && existingImageUrl) ||
        null;

  const inlineError = localError || error;
  const showFeedback =
    loading ||
    uploadSuccessLatched ||
    inlineError ||
    selectedFile ||
    preparePending;

  return (
    <section
      className="ap-pane is-active"
      aria-labelledby="ep-pane-photo"
      data-section="profile-photo"
    >
      <header className="ap-pane__head">
        <h2 id="ep-pane-photo">
          <i className="fa-solid fa-camera" aria-hidden="true"></i>Фотографія профілю
        </h2>
        <p>
          Завантажте фото для аватара. Рекомендований формат — квадратне зображення
          JPG, PNG або HEIC.
        </p>
      </header>

      <div className="ap-uploader">
        {displayImageUrl && (
          <div className="profile-photo-preview">
            <img
              src={displayImageUrl}
              alt="Попередній перегляд фото профілю"
              className="profile-photo-preview__img"
            />
          </div>
        )}
        {preparePending && !cropActive && (
          <div
            className="profile-photo-preview profile-photo-preview--pending-heic"
            role="status"
            aria-live="polite"
            data-prepare-state={prepareStatus?.state}
          >
            <span className="ap-upload__pending-label">
              {prepareStatus?.state === "server_preview_pending"
                ? "Підготовка попереднього перегляду на сервері…"
                : "Перевірка зображення…"}
            </span>
          </div>
        )}

        {cropActive && (
          <AvatarCropEditor
            sourceFile={pendingCropSource}
            title="Обрізати фото профілю"
            onApply={handleCropApply}
            onCancel={handleCropCancel}
            disabled={loading}
          />
        )}

        <label
          className={`ap-dropzone${isDragging ? " is-dragging" : ""}`}
          htmlFor="user-image-file"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            id="user-image-file"
            name="user_image"
            type="file"
            accept={IMAGE_ACCEPT}
            data-field="user_image"
            onChange={handleChange}
          />
          <span className="ap-dropzone__icon" aria-hidden="true">
            <i className="fa-solid fa-cloud-arrow-up"></i>
          </span>
          <span className="ap-dropzone__title">
            Перетягніть фото сюди або клацніть, щоб обрати
          </span>
          <span className="ap-dropzone__sub">{IMAGE_ACCEPT_HINT} до 10 MB</span>
        </label>

        {showFeedback && (
          <p className="profile-photo-feedback" role="status" aria-live="polite">
            {loading && (
              <span className="profile-photo-feedback--pending">
                Завантаження фото…
              </span>
            )}
            {!loading && uploadSuccessLatched && (
              <span className="profile-photo-feedback--success">
                Фото профілю оновлено.
              </span>
            )}
            {!loading && !uploadSuccessLatched && inlineError && (
              <span className="profile-photo-feedback--error">{inlineError}</span>
            )}
            {!loading && !uploadSuccessLatched && !inlineError && selectedFile && (
              <>
                <span className="profile-photo-feedback__filename">
                  {selectedFile.name}
                </span>
                <span className="profile-photo-feedback--ready">
                  Обрано. Збережіть форму, щоб завантажити фото.
                </span>
              </>
            )}
          </p>
        )}
      </div>
    </section>
  );
}

export default ProfilePhotoSection;
